// ─────────────────────────────────────────────────────────────────────────────
// PendingState — the active checkout view while awaiting the transfer.
//
// Composition:
//   Instruction text  → "Transfer to the account details below"
//   VA card           → bank name, VA number + copy, warning, account name,
//                       countdown
//   Status pulse      → "Waiting for your transfer…" with animated dot
//   CTA               → "I've transferred the money" — triggers immediate
//                       poll (no status change — server decides)
//
// Important rendering notes:
//
//   1. Some fields on PublicCheckoutResponse can be null even in pending
//      state:
//        - virtualAccount: rare race where the intent row exists before
//          the adapter allocated a VA. In practice this window is <1s
//          and polling will catch up. But we render defensively so the
//          user sees "Setting up your payment…" rather than blank.
//        - virtualAccountName: less critical; we fall back to the
//          merchantDisplayName if null.
//        - virtualAccountBank: fall back to a neutral "Posteering" label.
//        - expiresAt: null means no countdown. Handled inside <Countdown />.
//
//   2. The CTA does NOT confirm payment — only the server can do that via
//      webhook. Tapping it triggers onTransferClaimed() which asks the
//      polling hook for an immediate refresh. If the credit has arrived
//      between scheduled polls, the payer gets the completed state
//      faster than waiting for the next 5s tick.
//
//   3. We disable the CTA briefly after a click to prevent double-taps.
//      Real work happens async in the parent hook; the brief disable
//      is purely a UX affordance.
// ─────────────────────────────────────────────────────────────────────────────

import { useState, useCallback } from 'react';
import type { PublicCheckoutResponse } from '../types';
import { CopyButton } from './CopyButton';
import { Countdown } from './Countdown';

export interface PendingStateProps {
  data: PublicCheckoutResponse;
  onTransferClaimed: () => void;
}

export function PendingState({ data, onTransferClaimed }: PendingStateProps) {
  const [ctaCooldown, setCtaCooldown] = useState<boolean>(false);

  const handleCta = useCallback(() => {
    if (ctaCooldown) return;
    setCtaCooldown(true);
    onTransferClaimed();
    // Brief cooldown to prevent double-taps. Real work (the poll) happens
    // in the parent hook; this timer just re-enables the button.
    window.setTimeout(() => setCtaCooldown(false), 2000);
  }, [ctaCooldown, onTransferClaimed]);

  // Pre-allocation race: intent exists but VA not yet populated.
  // Rare (<1s window in practice) but we render honestly rather than
  // showing a blank VA number.
  if (data.virtualAccount === null) {
    return (
      <>
        <div className="instruction">Setting up your payment&hellip;</div>
        <div className="status-row">
          <span className="pulse" aria-hidden="true" />
          <span>Allocating your account&hellip;</span>
        </div>
      </>
    );
  }

  const bankLabel = data.virtualAccountBank ?? 'Posteering';
  const accountLabel = data.virtualAccountName ?? data.merchantDisplayName;

  return (
    <>
      <div className="instruction">Transfer to the account details below</div>

      <div className="va-card">
        <div className="bank-name">{bankLabel}</div>

        <div className="va-number-row">
          <span className="va-number">{data.virtualAccount}</span>
          <CopyButton
            value={data.virtualAccount}
            ariaLabel="Copy account number"
          />
        </div>

        <div className="va-warn">Do not save this account number</div>

        <div className="account-name-label">Account Name</div>
        <div className="account-name">{accountLabel}</div>

        <Countdown
          expiresAt={data.expiresAt}
          createdAt={data.createdAt}
        />
      </div>

      <div className="status-row">
        <span className="pulse" aria-hidden="true" />
        <span>Waiting for your transfer&hellip;</span>
      </div>

      <button
        type="button"
        className="cta"
        onClick={handleCta}
        disabled={ctaCooldown}
      >
        {ctaCooldown ? 'Checking\u2026' : "I've transferred the money"}
      </button>
    </>
  );
}
